<template>
  <u-layout class="layout-wrap">
    <u-sider
      v-show="!isHiddenLayout"
      :class="{ 'super-entry-product-sider': isSuperEntryLayout }"
      :width="showCollapseSidebar ? 80 : 248"
    >
      <div
        v-if="!showCollapseSidebar"
        key="expanded"
        class="sider-left pl-[16px] pb-[8px] pt-[16px]"
      >
        <div
          class="flex items-center justify-between h-[48px] mb-[8px] pt-[4px] pb-[8px] px-[2px] mr-[6px] pr-[10px]"
        >
          <img
            :src="logoSrc"
            alt=""
            height="30"
            class="cursor-pointer"
            data-testid="home-logo-button"
            @click="handleGoHome"
          />
          <BotIcon
            :name="showCollapseSidebar ? 'expand' : 'collapse'"
            :padding="0"
            class="cursor-pointer"
            data-testid="collapse-product-sidebar-button"
            size="24"
            @click="showCollapseSidebar = true"
          />
        </div>
        <div
          v-if="(isHomeOwner && hasHomeland) || (isCompanyUser && canAddAgent)"
          class="mb-[8px] pr-[8px]"
        >
          <CreateAgent />
        </div>
        <div>
          <u-button
            v-if="!hasHomeland"
            class="w-[214px] h-[32px] mb-[8px]"
            data-testid="create-space-button"
            icon="ULineAdd"
            type="primary"
            @click="handleCreateHome"
            >{{ $t('views.layout.Layout.b6d115e9f6bfe6d1a4d2eed3329c6fe2') }}
          </u-button>
        </div>
        <u-scrollbar dropdown-class="layout-scrollbar">
          <div class="mr-2 w-[216px]">
            <div
              v-if="hasHomeland && !isCompanyUser"
              class="flex flex-col mb-[12px]"
              data-testid="agent-store-menu-button"
              @click="handleGoAgentStore"
            >
              <div
                class="mt-2 mb-[6px] pl-2 leading-[20px] text-[12px] text-text-neutral-tertiary"
              >
                {{ $t('views.layout.Layout.d4f06af80e60abeb5a99a7b88e683782') }}
              </div>
              <div
                :class="{
                  'active-route': currentRouteName === 'agentStore'
                }"
                class="flex items-center pl-[16px] h-[32px] menu-item"
              >
                <BotIcon :padding="1" name="planet-line" size="16" />
                <span class="ml-[8px] user-select-none">{{
                  $t('views.layout.Layout.1f475609b7729fe65be127db16494da5')
                }}</span>
              </div>
            </div>
            <!-- Company users: enterprise agent group -->
            <div v-if="isCompanyUser" class="flex flex-col gap-1 mb-3 mt-3">
              <div
                class="pl-2 mb-[2px] leading-[20px] text-[12px] text-text-neutral-tertiary"
              >
                {{ $t('views.layout.Layout.7ecae10947f8d0bde18d27b79b15e390') }}
              </div>
              <div
                :class="{
                  'active-route': currentRouteName === 'enterprise'
                }"
                class="flex items-center pl-[16px] h-[32px] menu-item"
                data-testid="enterprise-workshop-menu-button"
                @click="handleGoWorkShop"
              >
                <BotIcon :padding="1" name="building-line" size="16" />
                <span
                  class="ml-[8px] user-select-none text-text-neutral route-name"
                  >{{
                    $t('views.layout.Layout.2e9650d8dab972f3d10c5e58858e3fc4')
                  }}</span
                >
              </div>
              <div
                v-permission="'monitor_market_menu'"
                :class="{
                  'active-route': currentRouteName === 'agentMonitor'
                }"
                class="flex items-center pl-[16px] h-[32px] menu-item"
                data-testid="agent-monitor-menu-button"
                @click="handleGoAgentMonitor"
              >
                <BotIcon :padding="1" name="speed-up-fill" size="16" />
                <span
                  class="ml-[8px] user-select-none text-text-neutral route-name"
                  >{{
                    $t('views.layout.Layout.740faa2968d3061f386b4d9ca5e88fb2')
                  }}</span
                >
              </div>
              <div
                v-if="isCompanyUser"
                v-permission="'appproval_management_menu'"
                :class="{
                  'active-route': currentRouteName === 'enterpriseApproval'
                }"
                class="flex items-center pl-[16px] h-[32px] menu-item"
                data-testid="enterprise-approval-menu-button"
                @click="handleNavigate('enterpriseApproval')"
              >
                <img
                  :src="approvalMenuIcon"
                  alt=""
                  class="size-[16px] flex-shrink-0"
                />
                <span
                  class="ml-[8px] user-select-none text-text-neutral route-name"
                  >{{
                    $t('views.layout.Layout.a056dae68a90fe6daac12ba396359f8c')
                  }}</span
                >
              </div>
              <div
                v-permission="'control_center_menu'"
                :class="{
                  'active-route': [
                    'agentManagement',
                    'agentManagementAgentAnalysis'
                  ].includes(currentRouteName)
                }"
                class="flex items-center pl-[16px] h-[32px] menu-item"
                data-testid="agent-management-menu-button"
                @click="handleGoAgentManagement"
              >
                <BotIcon :padding="1" name="pie-chart-line" size="16" />
                <span
                  class="ml-[8px] user-select-none text-text-neutral route-name"
                  >{{
                    $t('views.layout.Layout.d54ba2ddd33e79c5977f45f29c695982')
                  }}</span
                >
              </div>
            </div>

            <!-- Application group -->
            <div
              v-if="showBatchTask || canAccessSuperEntry"
              class="flex flex-col gap-1 mb-3"
            >
              <div class="layout-border w-[214px] mb-3" />
              <div
                class="pl-2 mb-[2px] leading-[20px] text-[12px] text-text-neutral-tertiary"
              >
                {{ $t('views.layout.Layout.c4073e91c7c480e3ba7550383e20f906') }}
              </div>
              <div
                v-if="canAccessSuperEntry"
                class="flex items-center pl-[16px] h-[32px] menu-item super-entry-menu"
                :class="{ 'active-route': isSuperEntryLayout }"
                data-testid="open-super-entry-menu"
                @click="handleGoSuperEntry"
              >
                <BotIcon :padding="1" name="super-entry" size="16" />
                <span class="ml-[8px] user-select-none route-name">
                  {{
                    $t('views.layout.Layout.64b068a53c0cc58def843d7a5ab74c26')
                  }}
                </span>
              </div>
              <div
                v-if="showBatchTask"
                v-permission="'batch_task_menu'"
                class="flex items-center pl-[16px] h-[32px] menu-item"
                :class="{
                  'active-route': currentRouteName === 'batchTask'
                }"
                data-testid="open-batch-task-menu"
                @click="handleNavigate('batchTask')"
              >
                <BotIcon name="batch" :padding="1" />
                <span
                  class="ml-[8px] user-select-none text-text-neutral route-name"
                  >{{
                    $t(
                      'views.layout.components.MenuList.807e92b0b10dce47f92dd97b3bdc8989'
                    )
                  }}</span
                >
              </div>
            </div>
            <div v-if="hasHomeland">
              <div>
                <div class="layout-border w-[214px] mb-3" />

                <div
                  class="text-[12px] text-text-neutral-tertiary leading-[20px] mb-[6px] user-select-none pl-2"
                >
                  {{
                    $t('views.layout.Layout.2cdcb000164483a08f8ddf82b4512be2')
                  }}
                </div>

                <MenuList :route-name="currentRouteName" />
              </div>
              <div v-if="showHardwareMenu" class="flex flex-col mt-[12px]">
                <div
                  class="text-[12px] text-text-neutral-tertiary leading-[20px] mb-[6px] user-select-none pl-2"
                >
                  {{
                    $t('views.layout.Layout.24813a5909414447f4430a0a4685af6f')
                  }}
                </div>
                <div class="flex flex-col gap-1 mb-3">
                  <div
                    class="flex items-center pl-[16px] h-[32px] menu-item rounded-[6px]"
                    data-testid="hardware-product-menu-button"
                    :class="{
                      'active-route': [
                        'hardwareProduct',
                        'hardwareProductDetail'
                      ].includes(currentRouteName)
                    }"
                    @click="handleNavigate('hardware/product')"
                  >
                    <BotIcon name="device-line" :padding="0" />
                    <span
                      class="ml-[8px] user-select-none text-text-neutral route-name"
                      >{{
                        $t(
                          'views.layout.Layout.69219e72af34133da289876ce94530b3'
                        )
                      }}</span
                    >
                  </div>
                  <div
                    class="flex items-center pl-[16px] h-[32px] menu-item rounded-[6px]"
                    data-testid="hardware-firmware-menu-button"
                    :class="{
                      'active-route': currentRouteName === 'hardwareFirmware'
                    }"
                    @click="handleNavigate('hardware/firmware')"
                  >
                    <BotIcon name="equipment-upgrade" :padding="0" />
                    <span
                      class="ml-[8px] user-select-none text-text-neutral route-name"
                      >{{
                        $t(
                          'views.layout.Layout.bd58856150636504d5a7523ca10d10e4'
                        )
                      }}</span
                    >
                  </div>
                  <div
                    class="flex items-center pl-[16px] h-[32px] menu-item rounded-[6px]"
                    data-testid="hardware-logs-menu-button"
                    :class="{
                      'active-route': currentRouteName === 'hardwareLogs'
                    }"
                    @click="handleNavigate('hardware/logs')"
                  >
                    <BotIcon name="device-log" :padding="0" />
                    <span
                      class="ml-[8px] user-select-none text-text-neutral route-name"
                      >{{
                        $t(
                          'views.layout.Layout.4309e10cd4cd880a8f64f5dc15956301'
                        )
                      }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="flex flex-col mt-[12px]">
                <div
                  class="text-[12px] text-text-neutral-tertiary leading-[20px] mb-[6px] user-select-none pl-2"
                >
                  {{
                    $t('views.layout.Layout.701e9fd011791e323bcb56dd62d5ddca')
                  }}
                </div>
                <OpsMenuList :route-name="currentRouteName" />
              </div>
              <div>
                <div
                  class="mt-3 text-[12px] text-text-neutral-tertiary leading-[20px] mb-[6px] user-select-none pl-2"
                >
                  {{
                    $t('views.layout.Layout.358777fc5ba428f5064ed8fd584849cf')
                  }}
                </div>
                <div class="flex flex-col gap-1.5 mb-3">
                  <div
                    v-permission="'homeland_menu'"
                    class="flex items-center pl-[16px] h-[32px] menu-item rounded-[6px]"
                    data-testid="space-settings-menu-button"
                    :class="{
                      'active-route': currentRouteName === 'homeland'
                    }"
                    @click="handleNavigate('homeland')"
                  >
                    <BotIcon name="settings" :padding="0" />
                    <span
                      class="ml-[8px] user-select-none text-text-neutral route-name"
                      >{{
                        $t(
                          'views.layout.components.UserInfoSetting.826a173f30ddb05ef4ed30498b92bc74'
                        )
                      }}</span
                    >
                  </div>
                  <div
                    v-if="isCompanyUser"
                    class="flex items-center pl-[16px] h-[32px] menu-item rounded-[6px]"
                    data-testid="log-audit-menu-button"
                    :class="{
                      'active-route': currentRouteName === 'logAudit'
                    }"
                    @click="handleNavigate('logAudit')"
                  >
                    <BotIcon name="log-line" :padding="0" />
                    <span
                      class="ml-[8px] user-select-none text-text-neutral route-name"
                      >{{
                        $t(
                          'views.layout.Layout.6740529666290c8e40c54ac71f1fd684'
                        )
                      }}</span
                    >
                  </div>
                  <div
                    class="flex items-center pl-[16px] h-[32px] menu-item rounded-[6px]"
                    data-testid="documentation-menu-button"
                    :class="{
                      'active-route': isDocCenterActive
                    }"
                    @click="handleDocCenter"
                  >
                    <BotIcon name="product" :size="16" :padding="0" />
                    <span
                      class="ml-[8px] user-select-none text-text-neutral route-name"
                      >{{
                        $t(
                          'views.layout.Layout.51de9f8ca7fc251b222db29188dae62c'
                        )
                      }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </u-scrollbar>
        <div
          :class="{ 'has-homeland-bottom': !hasHomeland }"
          class="flex h-[44px] mt-[16px] gap-2 items-center justify-between pr-4"
        >
          <div
            v-if="hasHomeland"
            class="centered-flex-container flex-1 min-w-0"
            :class="{ 'space-active': spaceMenuVisible }"
          >
            <ChangeSpace
              :style="changeSpaceStyle"
              @update:visible="val => (spaceMenuVisible = val)"
            />
          </div>
          <UserMenu :disabled="spaceMenuVisible">
            <BotIcon
              name="manu-meta-setting"
              :padding="0"
              class="cursor-pointer p-[4px] rounded-[6px] border border-[color:var(--border-neutral-color-split)] hover:bg-[color:var(--background-fill-white-color-fill-secondary)]"
              data-testid="open-user-menu-button"
              size="16"
            />
          </UserMenu>
        </div>
      </div>
      <CollapseSidebar
        v-else
        key="collapsed"
        :route-name="currentRouteName"
        :show-batch-task="showBatchTask"
        :show-collapse-sidebar="showCollapseSidebar"
        :show-super-entry="canAccessSuperEntry"
        @update:show-collapse-sidebar="handleExpand"
      />
    </u-sider>

    <u-content
      class="my-[8px] mr-2 layout-content h-full flex flex-col text-text-neutral-primary bg-container"
      :class="{
        'super-entry-content': isSuperEntryLayout,
        'batch-task-create-content': isBatchTaskCreatePage,
        'resource-plugin-space-content': isResourcePluginSpacePage,
        'ml-[8px]': isHiddenLayout
      }"
    >
      <div v-if="isSuperEntryLayout" class="h-full min-w-0 overflow-hidden">
        <router-view />
      </div>
      <u-scrollbar v-else ref="scrollbarRef" class="h-full w-full">
        <div class="min-w-[1184px] h-[calc(100vh-16px)]">
          <router-view />
        </div>
      </u-scrollbar>
    </u-content>
  </u-layout>
  <SpaceForm ref="spaceFormRef" />
</template>
<script>
import { useI18n } from 'vue-i18n';
import SpaceForm from '@/components/SpaceForm/index.vue';
import { logoSrc, layoutAddSrc } from '@/libs/img';
import approvalMenuIcon from '@/assetsCommon/settting/approval.png';
import { getBatchTaskWhitelistApi } from '@api/batchTask';
import { getUserRoleInfoApi } from '@api/homeland';
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  onUnmounted,
  nextTick
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getCompanyUserPermissionApi } from '@api/companyUserPermission';
import { usePermission } from '@/hooks/usePermission';
import MenuList from './components/MenuList.vue';
import CreateAgent from './components/CreateAgent.vue';
import CollapseSidebar from './components/CollapseSidebar.vue';
import UserMenu from './components/UserMenu.vue';
import ChangeSpace from './components/ChangeSpace.vue';
import OpsMenuList from './components/OpsMenuList.vue';
import isBaiyuHardwareEnabled, {
  baiyuHardwareMenuEnabled
} from '@libs/isBaiyuHardwareEnabled';
import useSuperEntryAvailability from '@/hooks/useSuperEntryAvailability';

export default defineComponent({
  name: 'Layout',
  components: {
    MenuList,
    CollapseSidebar,
    UserMenu,
    CreateAgent,
    ChangeSpace,
    SpaceForm,
    OpsMenuList
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const isHiddenLayout = computed(() => {
      return route.meta && route.meta.hidden;
    });
    const showCollapseSidebar = ref(false);
    const store = useStore();
    const isCompanyUser = computed(() => store.getters.isCompanyUser);
    const homelandInfo = computed(() => store.getters.homelandInfo);
    const hasHomeland = computed(() => {
      return homelandInfo.value.homeland;
    });
    const isHomeOwner = computed(() => {
      return homelandInfo.value.role === 'home_owner';
    });
    const enableWhite = ref(false);
    const whiteList = ref([]);
    const routerKey = computed(() => {
      if (route.path === '/botView') {
        return `${route.path}-${route.query.id}-${homelandInfo.value.homeland}`;
      }
      return `${route.path}-${Math.random()}-${homelandInfo.value.homeland}`;
    });
    const handleGoHome = () => {
      router.push('/');
    };
    const handleGoAgentStore = () => {
      router.push('/agentStore');
    };
    const handleGoAgentMonitor = () => {
      router.push('/agentMonitor');
    };
    const handleGoWorkShop = () => {
      router.push('/enterprise');
    };
    const handleGoAgentManagement = () => {
      router.push('/agentManagement');
    };
    const handleNavigate = (newRoute, permissionKey = null) => {
      if (permissionKey) {
        return;
      }
      router.push(`/${newRoute}`);
    };
    const handleDocCenter = () => {
      if (IS_DOMESTIC) {
        if (isBaiyuHardwareEnabled()) {
          router.push('/document/manual');
        } else {
          router.push('/docCenter');
        }
      } else {
        window.open('https://agentstudio.gitbook.io/agentstudio', '_blank');
      }
    };
    const isDocCenterActive = computed(() => {
      const name = router.currentRoute.value.name;
      return (
        typeof name === 'string' &&
        (name.startsWith('docCenter') || name.startsWith('document'))
      );
    });
    store.dispatch('getDoc');
    const avatar = computed(() => store.getters.botUserInfo?.avatar);
    const goDocument = () => {
      const url = store.state.options.docMap.product_doc;
      window.open(url, '_blank');
    };
    const showBatchTask = computed(() => {
      if (!enableWhite.value) return true;
      return whiteList.value.includes(store.getters.botUserInfo?.email);
    });
    const showHardwareMenu = computed(() => {
      route.fullPath;
      return baiyuHardwareMenuEnabled.value || isBaiyuHardwareEnabled();
    });
    const changeLang = ref(null);
    const bindEmail = ref(null);
    const modifyNickName = ref(null);
    const modifyPassword = ref(null);
    const spaceMenuVisible = ref(false);
    const nickName = computed(() => store.getters.botUserInfo?.nickname);
    const userInfo = ref({
      logo: ''
    });
    const { hasPermission, setPermissions } = usePermission();
    const canAddAgent = computed(() => {
      return hasPermission('work_space_manage_add');
    });
    const getCompanyUserPermission = async () => {
      const res = await getCompanyUserPermissionApi({
        homeland_id: homelandInfo.value.homeland
      });
      if (res.access) {
        localStorage.setItem('sbtToken', res.access);
      }
      const permissionList = res.resourcesList.map(item => item.englishName);
      setPermissions(permissionList);
    };
    getCompanyUserPermission();
    const getUserRoleInfo = async () => {
      const res = await getUserRoleInfoApi({
        homeland_id: homelandInfo.value.homeland,
        user_id: homelandInfo.value.user
      });
      if (res) {
        if (isCompanyUser.value) {
          return;
        }
        store.commit('updateHomelandId', {
          ...homelandInfo.value,
          role: res.user_role
        });
      }
    };
    getUserRoleInfo();
    const changeSpaceStyle = `padding: 0 8px 0 8px;border-radius: 6px;width: 100%;`;
    const currentRouteName = computed(() => {
      return router.currentRoute.value.name;
    });
    const isSuperEntryLayout = computed(() => {
      return route.matched.some(record => record.meta.superEntryLayout);
    });
    const isBatchTaskCreatePage = computed(() => {
      return [
        'batchTaskCreateEvaluationTask',
        'batchTaskCreateEvaluationSet'
      ].includes(route.name);
    });
    // The plugin library in the personal space (`/resource?tab=plugin&space=space`) renders its own
    // edge-to-edge panel, so it is the only page whose content must not be offset from the sider.
    // The space fallback mirrors the default used by `views/resource/index.vue`.
    const isResourcePluginSpacePage = computed(() => {
      return (
        route.name === 'resource' &&
        route.query.tab === 'plugin' &&
        (route.query.space || 'space') === 'space'
      );
    });
    const canAccessSuperEntry = computed(() => {
      // Space owners and enterprise administrators can access the Composer
      return ['home_owner', 'admin'].includes(homelandInfo.value.role);
    });
    const { hideTempSidebarEntries, ensureSuperEntryStatus } =
      useSuperEntryAvailability();
    // Load the super entry availability once the entry point becomes visible,
    // so the click target is already resolved before the user interacts.
    watch(
      canAccessSuperEntry,
      accessible => {
        if (accessible) ensureSuperEntryStatus();
      },
      { immediate: true }
    );
    // TODO(super-entry): when the temporary sidebar entries are hidden the menu
    // opens the Vibe Agent page, otherwise it opens the conversation page.
    const handleGoSuperEntry = () => {
      router.push({
        name: hideTempSidebarEntries.value
          ? 'superEntryVibeAgent'
          : 'superEntryConversation'
      });
    };
    const getBatchTaskWhitelist = async () => {
      const res = await getBatchTaskWhitelistApi();
      enableWhite.value = res.enable_white;
      whiteList.value = res.white_list;
    };
    getBatchTaskWhitelist();
    const spaceFormRef = ref(null);
    const handleCreateHome = () => {
      spaceFormRef.value.show();
    };
    const handleExpand = () => {
      showCollapseSidebar.value = false;
    };
    const scrollbarRef = ref(null);
    let resizeObserver = null;
    const handleResize = () => {
      nextTick(() => {
        if (
          scrollbarRef.value &&
          typeof scrollbarRef.value.update === 'function'
        ) {
          scrollbarRef.value.update();
        }
      });
    };
    onMounted(() => {
      window.addEventListener('resize', handleResize);
      resizeObserver = new MutationObserver(() => {
        handleResize();
      });
      const layoutContent = document.querySelector('.layout-content');
      if (layoutContent) {
        resizeObserver.observe(layoutContent, {
          attributes: true,
          attributeFilter: ['class', 'style'],
          childList: true,
          subtree: true
        });
      }
    });
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    });
    return {
      isHiddenLayout,
      handleGoHome,
      handleGoAgentStore,
      handleGoAgentMonitor,
      handleGoWorkShop,
      handleGoAgentManagement,
      routerKey,
      logoSrc,
      approvalMenuIcon,
      avatar,
      homelandInfo,
      layoutAddSrc,
      goDocument,
      bindEmail,
      changeLang,
      modifyNickName,
      modifyPassword,
      spaceMenuVisible,
      userInfo,
      showCollapseSidebar,
      changeSpaceStyle,
      currentRouteName,
      isSuperEntryLayout,
      isBatchTaskCreatePage,
      isResourcePluginSpacePage,
      canAccessSuperEntry,
      handleGoSuperEntry,
      isDocCenterActive,
      isHomeOwner,
      hasHomeland,
      spaceFormRef,
      handleCreateHome,
      isCompanyUser,
      hasPermission,
      canAddAgent,
      showBatchTask,
      showHardwareMenu,
      enableWhite,
      whiteList,
      nickName,
      handleExpand,
      handleNavigate,
      handleDocCenter,
      scrollbarRef,
      t
    };
  }
});
</script>
<style scoped lang="less">
.layout-wrap {
  height: 100vh;

  .logo {
    height: 32px;
    margin: 16px;
  }
}

@media (max-width: 767px) {
  .super-entry-product-sider {
    display: none !important;
  }

  .super-entry-content {
    margin-left: 0 !important;
  }
}

.sider-left {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: var(--color-text-icon-body-primary);
  overflow-y: auto;
  .middle-area {
    display: flex;
    flex-direction: column;
  }
}

.menu-item {
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background-color: var(--background-fill-white-color-fill-secondary);
  }
}

.active-route {
  background-color: var(--background-neutral-bg-color-elevated);
  color: var(--text-primary-color-primary-text);
  font-weight: 600;

  .route-name {
    color: var(--text-primary-color-primary-text);
  }

  &:hover {
    background-color: var(--background-neutral-bg-color-elevated);
  }
}

.super-entry-menu.active-route {
  background-color: transparent;
  background-image: linear-gradient(270deg, #fefaff 0%, #f7faff 89.29%);

  &:hover {
    background-color: transparent;
    background-image: linear-gradient(270deg, #fefaff 0%, #f7faff 89.29%);
  }
}

.super-entry-menu .route-name {
  background-image: linear-gradient(270deg, #fa67ff 0%, #0561ff 89.29%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.centered-flex-container {
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background-color: var(--background-fill-white-color-fill-secondary);
  }
}

.space-active {
  background-color: var(--background-neutral-bg-color-elevated);
  border-radius: 6px;
}

.has-homeland- bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

.layout-content {
  background-color: var(--background-neutral-bg-color-container);
  max-height: calc(100vh - 16px);
  border-radius: 12px;
}

// The personal-space plugin library (`/resource?tab=plugin&space=space`) keeps hugging the sider.
.layout-content.resource-plugin-space-content {
  margin-left: 0;
}

.layout-content.batch-task-create-content {
  margin-top: 0;
  margin-right: 0;
  margin-bottom: 0;
  max-height: 100vh;
  border-radius: 0;
}
.layout-border {
  border-top: 1px solid var(--border-neutral-color-split);
}
</style>
